import { memo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuraLayout, Layout } from "lola-framework-ui-test";
import { useStore } from "../store/globalState";
import { TITLE_COLOR } from "../constants";
import { LEGAL_DOC_KEYS, type LegalDocKey } from "../constants/legal";
import { useLegalDocument } from "../hooks/useLegalDocument";
import { getEvents, safeCapture } from "../analytics";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";


const isLegalDocKey = (value: string | null): value is LegalDocKey =>
  !!value && (LEGAL_DOC_KEYS as readonly string[]).includes(value);

/**
 * Renders a single legal document (E-Sign, User Agreement, etc.) as HTML
 * fetched from the backend legal proxy. The document is selected via the
 * `?doc=` query param; defaults to the user agreement.
 */
export const LegalPage = memo(() => {
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy } = colorConfig;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    safeCapture(getEvents().LEGAL_VIEW);
  }, []);

  const docParam = searchParams.get("doc");
  const doc: LegalDocKey = isLegalDocKey(docParam) ? docParam : "userAgreement";

  const { html, isLoading, error, retry } = useLegalDocument(doc);

  return (
    <AuraLayout colorConfig={colorConfig} className="legal-page">
      <Layout.Header>
        <Navbar
          onBackClick={() => navigate(-1)}
          title={t(`legal.docs.${doc}`)}
          align="center"
          color={TITLE_COLOR}
        />
      </Layout.Header>
      <Layout.Content isOverflowauto>
        <ScrollFadeIndicator color={bodyCopy} />
        <section className="legal-wrapper">
          {isLoading ? (
            <div className="legal-loading" style={{ color: bodyCopy }}>
              <div className="legal-skeleton" />
              <div className="legal-skeleton" />
              <div className="legal-skeleton" />
              <div className="legal-skeleton" />
              <span>{t("legal.loading")}</span>
            </div>
          ) : error || !html ? (
            <div className="legal-error" style={{ color: bodyCopy }}>
              <span>{t("legal.error")}</span>
              <button
                type="button"
                className="legal-retry-btn"
                style={{ color: TITLE_COLOR }}
                onClick={retry}
              >
                {t("legal.retry")}
              </button>
            </div>
          ) : (
            <div
              className="legal-content"
              style={{ color: bodyCopy }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
